import {
  buildingPoints, counts, dragRectCorner, formatStacks, isAxisRect, rectCorners, rotatePoints90, translatePoints, turnSchematic, bounds,
  type PlanObject, type PlanStore,
} from "@mcp/plan";
import type { Engine, PickResult, PointerHandler } from "../engine/Engine.ts";
import type { PlanRenderer } from "./PlanRenderer.ts";
import { select, useApp } from "../state/store.ts";

/** Clipboard format for copy/paste (also written to the system clipboard so it works across tabs). */
const CLIP_TAG = "mc-planner/objects@1";
interface Clip { tag: typeof CLIP_TAG; objects: PlanObject[] }

interface MoveDrag {
  kind: "move";
  start: [number, number];
  last: [number, number];
  originals: PlanObject[];
}
interface HandleDrag { kind: "handle"; id: string; handle: number }

/** Mouse/keyboard interaction for drawing and editing plan objects. */
export class PlanTools implements PointerHandler {
  private draft: [number, number][] = [];
  private hoverPt: [number, number] | null = null;
  /** Shift held at the last pointer move (building tool: rectangle from 2 corners) */
  private shift = false;
  private dragging: MoveDrag | HandleDrag | null = null;
  private clip: Clip | null = null;
  /** a name tag was pressed: it stands in for its object during this click/drag */
  private labelHit: { id: string; t: number } | null = null;
  private lastLabelDown = 0;

  /** time of the last press on the 3D canvas (forwarded tag presses arrive right after the tag's own handler) */
  private lastCanvasDown = 0;

  pointerDown() {
    this.lastCanvasDown = performance.now();
  }

  private takeLabelHit(): string | null {
    const h = this.labelHit;
    // only valid for the gesture that started on the tag (not for a later press on the canvas)
    return h && h.t <= this.lastCanvasDown && this.lastCanvasDown - h.t < 50 ? h.id : null;
  }

  /** the plan object under the pointer: a pressed name tag wins, else a raycast */
  private objectAt(e: MouseEvent): string | null {
    const lh = this.takeLabelHit();
    if (lh) return lh;
    const hit = this.engine.raycastPlans(e).find((h) => h.object.userData.planId);
    return hit ? (hit.object.userData.planId as string) : null;
  }
  private engine: Engine;
  private renderer: PlanRenderer;
  private store: PlanStore;
  onCursor: (p: PickResult | null) => void = () => {};
  /** home location actions (wired by the controller) */
  onGoHome: () => void = () => {};
  onSetHome: (at: { x: number; z: number } | null) => void = () => {};
  /** template placing mode (set by the controller): gets the mouse position, clicks and keys first */
  placement: { hover(pt: [number, number] | null): void; click(pt: [number, number], e: PointerEvent): void; key(e: KeyboardEvent): boolean } | null = null;

  constructor(engine: Engine, renderer: PlanRenderer, store: PlanStore) {
    this.engine = engine;
    this.renderer = renderer;
    this.store = store;
    renderer.onLabelPointerDown = (id, e) => {
      const now = performance.now();
      // our own double-click detection: after the first press the pointer is captured by the canvas,
      // so the browser's dblclick never reaches the tag
      if (e.button === 0 && this.labelHit?.id === id && now - this.lastLabelDown < 450) {
        this.lastLabelDown = 0;
        this.rename(id);
        return;
      }
      if (e.button === 0) this.lastLabelDown = now;
      this.labelHit = { id, t: now };
      engine.forwardToCanvas(e);
    };
    renderer.onLabelWheel = (e) => engine.forwardToCanvas(e);
    renderer.onLabelDblClick = (id) => this.rename(id);
  }

  get tool() {
    return useApp.getState().tool;
  }

  reset() {
    this.draft = [];
    this.renderer.setDraft(null, [], null);
    useApp.getState().set({ draftInfo: null });
  }

  private selectedObjects(): PlanObject[] {
    return useApp.getState().selectedIds.map((id) => this.store.get(id)).filter((o): o is PlanObject => !!o);
  }

  private selectClick(id: string | null, additive: boolean) {
    const cur = useApp.getState().selectedIds;
    if (!id) { if (!additive) select([]); return; }
    if (additive) select(cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
    else select([id]);
  }

  // ---------------- pointer ----------------

  hover(p: PickResult | null, e?: PointerEvent) {
    this.shift = !!e?.shiftKey;
    useApp.getState().set({ hover: p });
    this.onCursor(p);
    this.hoverPt = p ? [p.x, p.z] : null;
    if (this.placement) { this.placement.hover(this.hoverPt); return; }
    if (this.tool !== "select") this.updateDraft();
  }

  click(p: PickResult | null, e: PointerEvent) {
    const app = useApp.getState();
    if (this.placement) { if (p) this.placement.click([p.x, p.z], e); return; }
    if (this.tool === "select") {
      const id = this.objectAt(e);
      this.selectClick(id, e.shiftKey || e.ctrlKey || e.metaKey);
      // keep the tag as "last pressed" for double-click detection
      this.labelHit = this.labelHit && id === this.labelHit.id ? this.labelHit : null;
      return;
    }
    this.labelHit = null;
    if (!p) return;
    const pt: [number, number] = [p.x, p.z];
    const dim = app.dimension;
    switch (this.tool) {
      case "marker": {
        const o = this.store.create({ kind: "marker", points: [pt], y: p.y, dimension: dim, author: app.collab.name });
        this.finish(o);
        return;
      }
      case "building":
        // Shift+click the 2nd corner = quick rectangle (the old way)
        if (e.shiftKey && this.draft.length === 1 && (pt[0] !== this.draft[0][0] || pt[1] !== this.draft[0][1])) {
          this.createBuilding(rectCorners(this.draft[0], pt), p.y);
          return;
        }
      // fall through: click the outline corner by corner, like an area
      case "path":
      case "area": {
        const last = this.draft[this.draft.length - 1];
        if (last && last[0] === pt[0] && last[1] === pt[1]) return; // double-click duplicate
        if (this.tool !== "path" && this.draft.length >= 3 && pt[0] === this.draft[0][0] && pt[1] === this.draft[0][1]) { this.commit(); return; }
        this.draft.push(pt);
        this.updateDraft();
      }
    }
  }

  dblclick(_p: PickResult | null, e: MouseEvent) {
    if (this.tool === "path" || this.tool === "area" || this.tool === "building") { this.commit(); return; }
    if (this.tool === "select") {
      const id = this.objectAt(e);
      this.labelHit = null;
      if (id) this.rename(id);
    }
  }

  dragStart(p: PickResult | null, e: PointerEvent) {
    if (this.placement || this.tool !== "select" || e.button !== 0) return false;
    const app = useApp.getState();
    const hits = this.engine.raycastPlans(e);
    // 1) a corner handle of the (single) selected object
    const handle = hits.find((h) => h.object.userData.handle !== undefined && h.object.userData.planId === app.selectedId);
    if (handle && app.selectedIds.length === 1) {
      this.store.undo.stopCapturing();
      this.dragging = { kind: "handle", id: app.selectedId!, handle: handle.object.userData.handle as number };
      return true;
    }
    // 2) the body of a *selected* object → move the whole selection (unselected objects: normal camera pan)
    const lh = this.takeLabelHit();
    const body = lh ? (app.selectedIds.includes(lh) ? lh : null) : (hits.find((h) => h.object.userData.planId && app.selectedIds.includes(h.object.userData.planId as string))?.object.userData.planId as string | undefined) ?? null;
    if (!body || !p) return false;
    const originals = this.selectedObjects();
    if (!originals.length) return false;
    this.store.undo.stopCapturing();
    this.dragging = { kind: "move", start: [p.x, p.z], last: [0, 0], originals };
    return true;
  }

  drag(p: PickResult | null, e?: PointerEvent) {
    const d = this.dragging;
    if (!d || !p) return;
    if (d.kind === "handle") {
      const o = this.store.get(d.id);
      if (!o) return;
      const cur = buildingPoints(o);
      let pts = cur.map((q) => [...q] as [number, number]);
      // rectangular buildings stay rectangles when you drag a corner (Shift = move just this corner)
      if (o.kind === "building" && isAxisRect(cur) && !e?.shiftKey) pts = dragRectCorner(cur, d.handle, [p.x, p.z]);
      else pts[d.handle] = [p.x, p.z];
      if (pts.every((q, i) => q[0] === cur[i][0] && q[1] === cur[i][1]) && cur.length === o.points.length) return;
      const patch: Partial<PlanObject> = { points: pts };
      if (o.kind === "marker") patch.y = p.y;
      if (o.kind === "building") patch.y = this.groundY(pts, o.y);
      this.store.update(o.id, patch);
      return;
    }
    const dx = p.x - d.start[0], dz = p.z - d.start[1];
    if (dx === d.last[0] && dz === d.last[1]) return;
    d.last = [dx, dz];
    this.store.updateMany(d.originals.map((o) => ({ id: o.id, patch: this.movedPatch(o, dx, dz) })));
    useApp.getState().set({ draftInfo: `Moving ${d.originals.length > 1 ? d.originals.length + " objects " : ""}Δx ${dx >= 0 ? "+" : ""}${dx}  Δz ${dz >= 0 ? "+" : ""}${dz}` });
  }

  dragEnd() {
    if (this.dragging) this.store.undo.stopCapturing();
    this.dragging = null;
    this.labelHit = null;
    if (this.tool === "select") useApp.getState().set({ draftInfo: null });
  }

  /** New points (+ y that follows the terrain) of an object moved by (dx, dz). */
  private movedPatch(o: PlanObject, dx: number, dz: number): Partial<PlanObject> {
    const points = translatePoints(o.points, dx, dz);
    const patch: Partial<PlanObject> = { points };
    if (o.kind === "marker") patch.y = this.engine.heights.at(points[0][0], points[0][1]) ?? o.y;
    if (o.kind === "building") patch.y = this.groundY(points, o.y);
    return patch;
  }

  /** y for a new object: buildings on the median ground of their footprint, markers on the ground */
  seatY(o: Pick<PlanObject, "kind" | "points">, fallback: number): number {
    if (o.kind === "building") return this.groundY(o.points, fallback);
    if (o.kind === "marker" && o.points[0]) return this.engine.heights.at(o.points[0][0], o.points[0][1]) ?? fallback;
    return fallback;
  }

  /** median ground level under a building footprint (so it sits on the terrain) */
  private groundY(pts: [number, number][], fallback: number) {
    const r = bounds(pts);
    const hs: number[] = [];
    const step = Math.max(1, Math.floor(Math.max(r.x1 - r.x0, r.z1 - r.z0) / 32));
    for (let z = r.z0; z <= r.z1; z += step) for (let x = r.x0; x <= r.x1; x += step) { const h = this.engine.heights.at(x, z); if (h !== null) hs.push(h); }
    hs.sort((a, b) => a - b);
    return hs.length ? hs[Math.floor(hs.length / 2)] : fallback;
  }

  // ---------------- editing commands ----------------

  rename(id: string) {
    select([id]);
    // wait one frame so the label is rebuilt with the selection state before editing it
    requestAnimationFrame(() => this.renderer.startRename(id, (name) => this.store.update(id, { name })));
  }

  nudge(dx: number, dz: number) {
    const objs = this.selectedObjects();
    if (!objs.length) return;
    this.store.undo.stopCapturing();
    this.store.updateMany(objs.map((o) => ({ id: o.id, patch: this.movedPatch(o, dx, dz) })));
  }

  rotateSelection() {
    const objs = this.selectedObjects().filter((o) => o.points.length > 1);
    if (!objs.length) return;
    this.store.undo.stopCapturing();
    this.store.updateMany(objs.map((o) => {
      const points = rotatePoints90(o.points);
      // a schematic turns with its footprint and keeps its (maybe hand-adjusted) base height
      if (o.schematic) return { id: o.id, patch: { points, schematic: { ...o.schematic, ...turnSchematic(o.schematic, 1) } } };
      return { id: o.id, patch: o.kind === "building" ? { points, y: this.groundY(points, o.y) } : { points } };
    }));
  }

  /** Mirror selected schematic buildings east ↔ west (their footprint stays). */
  mirrorSchematics() {
    const objs = this.selectedObjects().filter((o) => o.schematic);
    if (!objs.length) return false;
    this.store.undo.stopCapturing();
    this.store.updateMany(objs.map((o) => ({ id: o.id, patch: { schematic: { ...o.schematic!, ...turnSchematic(o.schematic!, 0, true) } } })));
    return true;
  }

  deleteSelection() {
    const ids = useApp.getState().selectedIds;
    if (!ids.length) return;
    this.store.removeMany(ids);
    select([]);
  }

  /** Copy the selection. Returns the clipboard text (also kept internally). */
  copy(): string | null {
    const objects = this.selectedObjects();
    if (!objects.length) return null;
    this.clip = { tag: CLIP_TAG, objects };
    return JSON.stringify(this.clip);
  }

  /** Paste (from system clipboard text if it's ours, else the internal clipboard) at the block under the mouse. */
  paste(text?: string | null) {
    let clip = this.clip;
    if (text) {
      try {
        const c = JSON.parse(text) as Clip;
        if (c?.tag === CLIP_TAG && Array.isArray(c.objects)) clip = c;
      } catch { /* not ours */ }
    }
    if (!clip?.objects.length) return false;
    const all = clip.objects.flatMap((o) => o.points);
    const b = bounds(all);
    const at = this.hoverPt;
    const dx = at ? Math.round(at[0] - b.cx) : 5, dz = at ? Math.round(at[1] - b.cz) : 5;
    const dim = useApp.getState().dimension;
    this.store.undo.stopCapturing();
    const created = this.store.duplicate(clip.objects, dx, dz, dim);
    // re-seat on the terrain at the new place
    this.store.updateMany(created.filter((o) => o.kind === "building" || o.kind === "marker").map((o) => ({ id: o.id, patch: this.movedPatch(o, 0, 0) })));
    select(created.map((o) => o.id));
    useApp.getState().set({ tool: "select" });
    return true;
  }

  duplicateSelection() {
    const objs = this.selectedObjects();
    if (!objs.length) return;
    this.store.undo.stopCapturing();
    const created = this.store.duplicate(objs, 5, 5);
    this.store.updateMany(created.filter((o) => o.kind === "building" || o.kind === "marker").map((o) => ({ id: o.id, patch: this.movedPatch(o, 0, 0) })));
    select(created.map((o) => o.id));
  }

  // ---------------- keyboard ----------------

  /**
   * Enter = finish, Escape = cancel, Backspace = undo last point, Delete = remove selection,
   * Alt+arrows = move selection (Shift = 10 blocks), R = rotate 90°, F2 = rename, Ctrl+D = duplicate,
   * 1–5 = tools, H = go home, Shift+H = set home at the mouse. (WASD/QE/+- camera keys: Engine.setKey.)
   * (Ctrl+C / Ctrl+V are handled through the browser's copy/paste events — see controller.)
   */
  key(e: KeyboardEvent): boolean {
    const t = e.target as HTMLElement;
    if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return false;
    const app = useApp.getState();
    const mod = e.ctrlKey || e.metaKey;
    if (this.placement && this.placement.key(e)) return true;
    if (e.key === "Escape") {
      if (this.draft.length) this.reset();
      else if (app.tool !== "select") app.set({ tool: "select" });
      else select([]);
      return true;
    }
    if (e.key === "Enter" && this.draft.length) { this.commit(); return true; }
    if (e.key === "Backspace" && this.draft.length) { this.draft.pop(); this.updateDraft(); return true; }
    if ((e.key === "Delete" || e.key === "Backspace") && app.selectedIds.length) { this.deleteSelection(); return true; }
    if (mod && e.key.toLowerCase() === "z") {
      if (e.shiftKey) this.store.undo.redo(); else this.store.undo.undo();
      return true;
    }
    if (mod && e.key.toLowerCase() === "y") { this.store.undo.redo(); return true; }
    if (mod && e.key.toLowerCase() === "d") { this.duplicateSelection(); return true; }
    if (mod && e.key.toLowerCase() === "a") { select(app.plans.filter((p) => p.dimension === app.dimension).map((p) => p.id)); return true; }
    if (e.key === "F2" && app.selectedId) { this.rename(app.selectedId); return true; }
    if (e.altKey && app.selectedIds.length && e.key.startsWith("Arrow")) {
      const n = e.shiftKey ? 10 : 1;
      const [dx, dz] = { ArrowLeft: [-n, 0], ArrowRight: [n, 0], ArrowUp: [0, -n], ArrowDown: [0, n] }[e.key] ?? [0, 0];
      this.nudge(dx, dz);
      return true;
    }
    if (!mod && !e.altKey && e.code === "KeyR" && app.selectedIds.length && app.tool === "select") { this.rotateSelection(); return true; }
    if (!mod && !e.altKey && e.code === "KeyF" && app.selectedIds.length && app.tool === "select" && this.mirrorSchematics()) return true;
    // tools: 1–5 (letters V/P/B/M still work; A is now "move left")
    const map: Record<string, typeof app.tool> = {
      Digit1: "select", Digit2: "path", Digit3: "area", Digit4: "building", Digit5: "marker",
      Numpad1: "select", Numpad2: "path", Numpad3: "area", Numpad4: "building", Numpad5: "marker",
      KeyV: "select", KeyP: "path", KeyB: "building", KeyM: "marker",
    };
    if (!mod && !e.altKey && !e.shiftKey && map[e.code]) {
      this.reset();
      app.set({ tool: map[e.code] });
      return true;
    }
    if (!mod && !e.altKey && e.code === "KeyH") {
      if (e.shiftKey) this.onSetHome(this.hoverPt ? { x: this.hoverPt[0], z: this.hoverPt[1] } : null);
      else this.onGoHome();
      return true;
    }
    return false;
  }

  private commit() {
    const app = useApp.getState();
    const kind = this.tool;
    if (kind === "path" && this.draft.length >= 2) {
      this.finish(this.store.create({ kind: "path", points: this.draft, dimension: app.dimension, author: app.collab.name }));
    } else if (kind === "area" && this.draft.length >= 3) {
      this.finish(this.store.create({ kind: "area", points: this.draft, dimension: app.dimension, author: app.collab.name }));
    } else if (kind === "building" && this.draft.length >= 2) {
      // 2 corners → rectangle, 3+ → footprint polygon
      this.createBuilding(this.draft.length === 2 ? rectCorners(this.draft[0], this.draft[1]) : this.draft, 64);
    }
  }

  private createBuilding(points: [number, number][], fallbackY: number) {
    const app = useApp.getState();
    this.finish(this.store.create({ kind: "building", points, y: this.groundY(points, fallbackY), dimension: app.dimension, author: app.collab.name }));
  }

  /** points placed so far of the object being drawn (0 = not drawing) */
  get draftPoints() {
    return this.draft.length;
  }

  /** a path / area / building / marker was drawn (starter checklist, tour) */
  onCreated: (o: PlanObject) => void = () => {};

  private finish(o: PlanObject) {
    this.onCreated(o);
    this.reset();
    select([o.id]);
    useApp.getState().set({ tool: "select" });
  }

  /** the outline the building tool would create right now (with the mouse position) */
  private buildingDraft(): [number, number][] {
    const h = this.hoverPt;
    if (this.draft.length === 1 && h && this.shift) return rectCorners(this.draft[0], h);
    return h ? [...this.draft, h] : this.draft;
  }

  private updateDraft() {
    const kind = this.tool === "select" ? null : this.tool;
    const pts = this.hoverPt ? [...this.draft, this.hoverPt] : this.draft;
    if (kind === "building") this.renderer.setDraft("building", this.buildingDraft(), null, 8);
    else this.renderer.setDraft(kind === "marker" ? null : kind, this.draft, this.hoverPt);
    let info: string | null = null;
    const n = (v: number) => v.toLocaleString("en-US");
    if (kind === "path" && this.draft.length) {
      const c = counts({ kind: "path", points: pts, width: 3, height: 0 });
      info = `${n(c.length ?? 0)} long · ${n(c.blocks)} blocks at width 3 · Enter/double-click to finish`;
    }
    if (kind === "area" && this.draft.length) {
      const c = pts.length >= 3 ? counts({ kind: "area", points: pts, width: 0, height: 0 }) : null;
      info = `${c ? `${c.approx ? "≈" : ""}${n(c.blocks)} blocks · ${c.w} × ${c.d} · ` : ""}click first point or Enter to close`;
    }
    if (kind === "building" && this.draft.length) {
      const outline = this.buildingDraft();
      const c = outline.length >= 2 ? counts({ kind: "building", points: outline.length === 2 ? rectCorners(outline[0], outline[1]) : outline, width: 0, height: 8 }) : null;
      const hint = this.draft.length === 1
        ? "click the next corner · Shift+click = rectangle"
        : this.draft.length === 2 ? "next corner, or Enter = rectangle" : "click first corner or Enter to finish";
      info = `${c ? `${c.w} × ${c.d} · ${n(c.blocks)} footprint (${formatStacks(c.blocks)}) · ` : ""}${hint}`;
    }
    if (!this.draft.length && kind) info = { path: "Click to add path points", area: "Click to add area corners", building: "Click the corners of the footprint (Shift+click 2nd corner = rectangle)", marker: "Click to place marker" }[kind];
    useApp.getState().set({ draftInfo: info });
  }
}
